import React, { useState, useEffect, useRef } from "react";
import TextHighlight from "../dumb/TextHighlight";
import { TextArea } from "../dumb/DataTable";
import Check from "../../images/check.png";
import Improve from "../../images/improve.png";
import Tooltip from "../dumb/Tooltip";

const TaskEditorRow = (props) => {
  const [hasErrors, setHasErrors] = useState(!!props.hasErrors);
  const [spellRows, setSpellRows] = useState([]);
  const [loading, setLoading] = useState(false); // NEW
  const prevCompletedRef = React.useRef(props.completed);
  const pendingSpellcheckRef = React.useRef(false);
  const runSpellcheckRef = useRef(false);
  const didMountRef = useRef(false);
  const alreadyCheckedWords = useRef([]);
  const alreadyCheckedErrors = useRef([]);

  const latestTargetRef = useRef(props.target);

  useEffect(() => {
    latestTargetRef.current = props.target;
  }, [props.target]);

  const latestHighlightWordsRef = useRef(props.highlightWords);
  const latestTmMatchWordsRef = useRef(props.tmMatchWords);

  // Update refs whenever props change
  useEffect(() => {
    latestHighlightWordsRef.current = props.highlightWords;
    latestTmMatchWordsRef.current = props.tmMatchWords;
  }, [props.highlightWords, props.tmMatchWords]);

  const handleTargetInput = (val) => {
    latestTargetRef.current = val; // always newest typed text
    props.onTargetInput(val); // existing parent handler (debounced save)
  };

  const fetchQaResults = async () => {
    try {
      const res = await fetch(
        `${window.API_URL}/task/getQaResults.php?taskID=${props.rowID}`,
      );

      const data = await res.json();
      console.log("Data", data);
      const grouped = (data || []).reduce((acc, row) => {
        if (!acc[row.qa_row_id]) {
          acc[row.qa_row_id] = {
            lang: row.qa_lang || "",
            // store qa_lang from first occurrence
            issues: [],
          };
        }
        acc[row.qa_row_id].issues.push(row);
        return acc;
      }, {});

      alreadyCheckedWords.current = [];
      Object.values(grouped).forEach(({ issues }) => {
        issues.forEach((issue) => {
          if (
            issue.qa_context?.startsWith("Spelling Error:") &&
            issue.qa_status !== "i"
          ) {
            // Extracts "clavesir" from "Spelling Error: clavesir. -> clave sir."
            const match = issue.qa_context.match(
              /Spelling Error:\s*(.+?)\s*->\s*(.+)/,
            );
            if (match) {
              alreadyCheckedWords.current.push(match[1].trim());
              alreadyCheckedErrors.current.push(match[2].trim());
            }
          }
        });
      });
      console.log("Fetched QA results", grouped);
    } catch (err) {
      console.error("Failed to load QA results", err);
    }
  };
  /*
  useEffect(() => {
    setHasErrors("1");
  }, []);*/

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /**
   * Counts occurrences of `needle` inside `haystack`.
   * - If `wholeWord=true`, matches whole words for single-word needles (uses \b boundaries).
   * - For phrases (contains whitespace), it matches as a plain substring with flexible whitespace.
   */
  function countOccurrences(
    haystack,
    needle,
    { wholeWord = true, caseInsensitive = true } = {},
  ) {
    if (!haystack || !needle) return 0;

    const raw = needle.trim();
    if (!raw) return 0;

    const flags = `g${caseInsensitive ? "i" : ""}`;

    // If it's a multi-word phrase, allow any whitespace between words
    if (/\s/.test(raw)) {
      const parts = raw.split(/\s+/).map(escapeRegExp);
      const pattern = parts.join("\\s+");
      const re = new RegExp(pattern, flags);
      return (haystack.match(re) || []).length;
    }

    // Single word
    const word = escapeRegExp(raw);
    const pattern = wholeWord ? `\\b${word}\\b` : word;
    const re = new RegExp(pattern, flags);
    return (haystack.match(re) || []).length;
  }

  useEffect(() => {
    setHasErrors(!!props.hasErrors);
  }, [props.hasErrors]);

  const spellCheck = async (
    rowSource,
    rowData,
    languageCode,
    high = [],
    matching = [],
  ) => {
    await fetchQaResults(); // refresh alreadyCheckedWords before spellcheck
    setLoading(true); // show spinner

    try {
      const TAG_REGEX = /<[^<>]*?>|\{[^{}]*\}|\[[^\[\]]*\]/g;

      const stripTagsForSpellcheck = (text) =>
        (text || "").replace(TAG_REGEX, " ").replace(/\s+/g, " ").trim();
      const isSpellWord = (w) => /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(w);
      //const words = rowData.split(" ");
      const spellText = stripTagsForSpellcheck(rowData);
      const words = spellText.split(" ").filter(Boolean).filter(isSpellWord);

      const uncheckedWords = words.filter(
        (word) => !alreadyCheckedWords.current.includes(word),
      );
      console.log(
        "Words to check (after filtering already checked)",
        uncheckedWords,
      );
      const response = await fetch(`${window.API_URL}/task/spellcheck.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: uncheckedWords, language: languageCode }),
      });

      const data = await response.json();
      const original = data.original || [];
      const corrected = data.corrected || [];
      const resultRows = [];

      corrected.forEach((word, index) => {
        if (word !== original[index]) {
          resultRows.push({
            type: "correction",
            original: original[index],
            corrected: word,
          });
        }
      });

      alreadyCheckedWords.current.forEach((word, index) => {
        resultRows.push({
          type: "correction",
          original: word,
          corrected: alreadyCheckedErrors.current[index],
        });
      });

      const n = Math.min(high.length, matching.length);
      for (let i = 0; i < n; i++) {
        const srcPhrase = high[i];
        const tgtPhrase = matching[i];

        if (!srcPhrase || !tgtPhrase) continue;

        const srcCount = countOccurrences(rowSource, srcPhrase, {
          wholeWord: true,
        });
        if (srcCount === 0) continue;

        const tgtCount = countOccurrences(rowData, tgtPhrase, {
          wholeWord: true,
        });

        if (tgtCount < srcCount) {
          const missing = srcCount - tgtCount;
          resultRows.push({
            type: "warning",
            message:
              missing === 1
                ? `Glossary mismatch: "${srcPhrase}" appears ${srcCount}× in source but "${tgtPhrase}" appears ${tgtCount}× in target (missing 1)`
                : `Glossary mismatch: "${srcPhrase}" appears ${srcCount}× in source but "${tgtPhrase}" appears ${tgtCount}× in target (missing ${missing})`,
          });
        }
      }
      // Extract full numbers + ordinals (1st/2nd/3rd/13th), including simple decimals (3.14 / 3,14)
      const extractNumberTokens = (text) => {
        const clean = stripTagsForSpellcheck(text);

        // 1) ordinals first, so "1st" isn't partially treated as "1"
        // 2) numbers (optionally with . or , decimals)
        //const re = /\b\d+(?:st|nd|rd|th)\b|\b\d+(?:[.,]\d+)*\b/gi;
        const re =
          /\b\d+(?:st|nd|rd|th)\b|\b\d+(?:[.,]\d+)*\b|\b[A-Za-z_]*\d+[A-Za-z_]*\b/gi;

        return (clean.match(re) || []).map((t) => t.toLowerCase());
      };

      // Multiset diff: returns what A has that B doesn't (by token count)
      const diffTokens = (aTokens, bTokens) => {
        const count = (arr) => {
          const m = new Map();
          for (const t of arr) m.set(t, (m.get(t) || 0) + 1);
          return m;
        };

        const a = count(aTokens);
        const b = count(bTokens);

        const diff = [];
        for (const [token, aCount] of a.entries()) {
          const bCount = b.get(token) || 0;
          const missingCount = aCount - bCount;
          if (missingCount > 0) {
            diff.push(
              missingCount === 1 ? token : `${token} (x${missingCount})`,
            );
          }
        }
        return diff;
      };

      // --- Numbers / ordinals consistency check (source vs target) ---
      const sourceTokens = extractNumberTokens(rowSource);
      const targetTokens = extractNumberTokens(rowData);

      // Source numbers missing in Target
      const missingInTarget = diffTokens(sourceTokens, targetTokens);
      if (missingInTarget.length > 0) {
        resultRows.push({
          type: "warning",
          message: `Missing numbers in target: ${missingInTarget.join(", ")}`,
        });
      }

      // Target has numbers that Source doesn't (extra numbers)
      const extraInTarget = diffTokens(targetTokens, sourceTokens);
      if (extraInTarget.length > 0) {
        resultRows.push({
          type: "warning",
          message: `Extra numbers in target (not in source): ${extraInTarget.join(
            ", ",
          )}`,
        });
      }

      //const originalDigits = rowSource.match(/\d/g) || [];
      //const correctedDigits = corrected.join(" ").match(/\d/g) || [];
      /* const originalDigits = rowSource.match(/\d+/g) || [];
      const correctedDigits = original.join(" ").match(/\d+/g) || [];

      if (correctedDigits.length === 0 && originalDigits.length > 0) {
        resultRows.push({
          type: "warning",
          message: `Missing digits: ${[...new Set(originalDigits)].join(", ")}`,
        });
      } else {
        const missingDigits = originalDigits.filter(
          (d) => !correctedDigits.includes(d)
        );
        if (missingDigits.length > 0) {
          resultRows.push({
            type: "warning",
            message: `Missing digits: ${[...new Set(missingDigits)].join(
              ", "
            )}`,
          });
        }
      }*/

      if (!rowData || rowData.length === 0) {
        resultRows.push({
          type: "warning",
          message: "Empty target",
        });
      }

      const correctedText = rowData;

      /*// --- TAG CHECK ( <...>  {...}  [...] ) ---
      const TAG_REGEX = /<[^<>]*?>|\{[^{}]*\}|\[[^\[\]]*\]/g;

      const getTagCounts = (text) => {
        const matches = text.match(TAG_REGEX) || [];
        const counts = new Map();
        for (const m of matches) counts.set(m, (counts.get(m) || 0) + 1);
        return counts;
      };

      const sourceTags = getTagCounts(rowSource);
      const targetTags = getTagCounts(correctedText);

      for (const [tag, srcCount] of sourceTags.entries()) {
        const tgtCount = targetTags.get(tag) || 0;

        // If you want strict count matching: target must contain tag at least srcCount times
        if (tgtCount < srcCount) {
          resultRows.push({
            type: "warning",
            message: `Missing tag: ${tag}`,
          });
        }
      }
      // --- END TAG CHECK ---*/

      const getTagCounts = (text) => {
        const matches = text.match(TAG_REGEX) || [];
        const counts = new Map();
        for (const m of matches) counts.set(m, (counts.get(m) || 0) + 1);
        return counts;
      };

      // use correctedText (your current behavior) or rowData if you prefer the raw target
      const sourceTags = getTagCounts(rowSource);
      const targetTags = getTagCounts(correctedText);

      // Missing tags (source -> target)
      for (const [tag, srcCount] of sourceTags.entries()) {
        const tgtCount = targetTags.get(tag) || 0;
        if (tgtCount < srcCount) {
          resultRows.push({
            type: "warning",
            message:
              srcCount - tgtCount === 1
                ? `Missing tag: ${tag}`
                : `Missing tag: ${tag} (x${srcCount - tgtCount})`,
          });
        }
      }

      // Extra tags (target -> source)
      for (const [tag, tgtCount] of targetTags.entries()) {
        const srcCount = sourceTags.get(tag) || 0;
        if (tgtCount > srcCount) {
          resultRows.push({
            type: "warning",
            message:
              tgtCount - srcCount === 1
                ? `Extra tag: ${tag}`
                : `Extra tag: ${tag} (x${tgtCount - srcCount})`,
          });
        }
      }
      // --- END TAG CHECK ---

      if (/^\s|\s$/.test(correctedText)) {
        resultRows.push({
          type: "warning",
          message: "Leading or trailing space",
        });
      }

      const repeatedWordsMatch = correctedText.match(/\b(\w+)(?:\s+\1\b)+/gi);
      if (repeatedWordsMatch) {
        repeatedWordsMatch.forEach((match) => {
          const word = match.split(" ")[0];
          resultRows.push({
            type: "warning",
            message: `Repeated word: work "${word}"`,
          });
        });
      }

      const doubleSpaceRegex = /(\S+)(\s{2,})(\S+)/g;
      let match;
      while ((match = doubleSpaceRegex.exec(rowData)) !== null) {
        resultRows.push({
          type: "warning",
          message: `Double space between:"${match[1]}" "${match[3]}"`,
        });
      }

      const ignRes = await fetch(
        `${window.API_URL}/task/getQaIgnored.php?taskID=${props.rowID}`,
      );

      const ignored = await ignRes.json();
      const normalize = (s) =>
        String(s ?? "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();

      // ignored rows coming from API:
      // [{ qa_context, qa_type, qa_id, qa_row_id, ... }, ...]
      const ignoredContexts = new Set(
        (ignored || []).map((x) => normalize(x.qa_context)),
      );

      const toCtx = (r) => {
        if (r.type === "correction") {
          // MUST match how you saved qa_context in DB (case doesn't matter due to normalize)
          return `Spelling Error: ${r.original} -> ${r.corrected}`;
        }

        // For warnings like "Leading or trailing space"
        // Prefer qa_context if you stored it, otherwise message
        return r.qa_context ?? r.message;
      };

      const finalRows = (resultRows || []).filter((r) => {
        const ctx = normalize(toCtx(r));
        return !ignoredContexts.has(ctx);
      });

      await fetch(`${window.API_URL}/task/saveQaResults.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskID: props.taskID,
          rowID: props.rowID,
          lang: languageCode,
          results: resultRows,
        }),
      });

      // Fetch back the saved results with their statuses
      const savedRes = await fetch(
        `${window.API_URL}/task/getQArow.php?taskID=${props.taskID}`,
      );
      const savedData = await savedRes.json();
      const thisRow = (savedData || []).find(
        (r) => String(r.rowID) === String(props.rowID),
      );

      // True if every error for this row is status "i"
      const allErrorsAreInfo = thisRow?.qa_status === "i";
      console.log("final rows", finalRows);
      if (resultRows.length > 0) {
        setHasErrors(true);
        props.onErrorStateChange?.(props.rowID, true);
        if (!allErrorsAreInfo) {
          props.setCompleted(0); // block completion only if non-"i" errors exist
        }
        setSpellRows(resultRows);
      } else {
        setHasErrors(false);
        props.onErrorStateChange?.(props.rowID, false);
        props.setCompleted(1);
        setSpellRows([]);
      }
    } catch (error) {
      console.error("Spellcheck failed:", error);
    } finally {
      setLoading(false); // hide spinner
    }
  };

  const overCharLimit =
    props.charLimit && props.target?.length > props.charLimit;

  const highlightsReady = Array.isArray(props.highlightWords);

  const tmReady = Array.isArray(props.tmMatchWords);

  const canCheck =
    props.suggestionsLoaded &&
    highlightsReady &&
    tmReady &&
    !loading &&
    !props.confirmPending;

  const isPending = props.selected && !canCheck;

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevCompletedRef.current = props.completed;
      return;
    }
    const prev = prevCompletedRef.current;
    const now = props.completed;
    prevCompletedRef.current = now;
    const high = Array.isArray(latestHighlightWordsRef.current)
      ? latestHighlightWordsRef.current
      : [];
    const match = Array.isArray(latestTmMatchWordsRef.current)
      ? latestTmMatchWordsRef.current
      : [];
    if (prev != 1 && now == 1) {
      if (canCheck) {
        // only when changing from not-completed -> completed

        (async () => {
          await spellCheck(
            props.source,
            //latestTargetRef.current,
            props.target,
            props.targetLangIso,
            high,
            match,
          );
        })();
      } else {
        // if we can't run it yet (loading TM matches etc), defer
        pendingSpellcheckRef.current = true;
      }
    }
  }, [
    props.completed,

    props.source,
    props.target,
    props.targetLangIso,
    props.highlightWords,
    props.tmMatchWords,
  ]);

  useEffect(() => {
    if (!props.confirmNonce) return;

    (async () => {
      // mimic checkbox behavior: mark completed, then run spellcheck,
      runSpellcheckRef.current = true; // and spellcheck will un-complete if errors are found.
      props.setCompleted(1);

      const high = Array.isArray(props.highlightWords)
        ? props.highlightWords
        : [];
      const match = Array.isArray(props.tmMatchWords) ? props.tmMatchWords : [];

      await spellCheck(
        props.source,
        //latestTargetRef.current,
        props.target,
        props.targetLangIso,
        high,
        match,
      );

      props.onConfirmDone?.(props.rowID, props.confirmNonce);
    })();
  }, [props.confirmNonce]);

  return (
    <tr
      onClick={props.rowSelected}
      key={props.ID}
      className={
        "table__row " +
        (props.modified ? "task-editor__row--unsaved " : "") +
        (props.selected ? "table__row--selected" : "")
      }
    >
      <td className="task-editor__td table__data">{props.ID}</td>
      <td className="task-editor__td table__data">
        <div className="task-editor__source">
          <TextHighlight
            text={props.source}
            textTarget={props.target}
            highlight={props.highlightWords}
            matchTmTarget={props.tmMatchWords}
            className="highlight__glossary-term"
          />
        </div>
      </td>
      <td className="task-editor__td table__data task-editor__td-target table__data--textarea">
        <TextArea
          spellcheck={false}
          className="task-editor__target-input"
          value={props.target}
          onInput={handleTargetInput}
          onFocus={props.rowSelected}
          focus={props.selected}
        />
      </td>
      <td
        onClick={props.genderSplit}
        className="task-editor__td table__data task-editor__td--center"
      >
        {props.gender}
      </td>
      <td className="task-editor__td table__data task-editor__td--center">
        <Tooltip text={<pre>CHAR COUNT CHAR LIMIT</pre>}>
          <div className="task-editor__target-char-count tooltip-trigger">
            <span
              className={
                overCharLimit ? "task-editor__target-char-count--excess" : ""
              }
            >
              {props.target?.length}
            </span>
            {props.charLimit ? <span> {props.charLimit}</span> : ""}
          </div>
        </Tooltip>
      </td>

      <td className="task-editor__td table__data task-editor__td--center">
        <div className="task-editor__target-char-count tooltip-trigger">
          {loading || props.confirmPending ? (
            <div className="spinner" style={{ margin: "0px" }} />
          ) : props.hasErrors || hasErrors ? ( // <- parent or local
            <a
              href={`javascript:window.open('/task/quality/${props.rowID}/${props.taskID}', '_blank', 'height=600,width=800')`}
            >
              <img
                src={Improve}
                alt="Improve suggestion"
                style={{ cursor: "pointer" }}
              />
            </a>
          ) : null}
        </div>
      </td>

      <td className="task-editor__td table__data task-editor__td--center">
        <div className="checkbox-container">
          <input
            id={"check" + props.ID}
            tabIndex="-1"
            onFocus={props.rowSelected}
            disabled={isPending} // optional: prevent checking while red
            onChange={(e) => {
              if (!canCheck) return;
              const checked = e.target.checked;
              if (checked) runSpellcheckRef.current = true;

              props.setCompleted(checked ? 1 : 0);
            }}
            className={"checkbox task-editor__completed"}
            checked={props.completed == 1}
            type="checkbox"
          />
          <label
            className={
              isPending
                ? "checkbox-container--pending"
                : "checkbox-container-label"
            }
            htmlFor={"check" + props.ID}
          ></label>
          <img src={Check} alt="Completed" />
        </div>
      </td>
    </tr>
  );
};

export default React.memo(TaskEditorRow, (prevProps, nextProps) => {
  return (
    prevProps.target == nextProps.target &&
    prevProps.completed == nextProps.completed &&
    prevProps.modified == nextProps.modified &&
    prevProps.selected == nextProps.selected &&
    prevProps.highlightWords == nextProps.highlightWords &&
    prevProps.tmMatchWords == nextProps.tmMatchWords &&
    prevProps.hasErrors == nextProps.hasErrors
  );
});
